// Main Utilities Dashboard Component
// Shows all plants horizontally with current month and 6-month average data
// UPDATED: Added Print and Export to Excel buttons matching Flash Report style

import React, { useState, useEffect } from 'react';
import { utilitiesAPI } from '../utils/api';
import '../styles/MainDashboard.css';

function UtilitiesDashboard() {
    const [reportMonth, setReportMonth] = useState('');
    const [dashboardData, setDashboardData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load dashboard data when component mounts
    useEffect(() => {
        const now = new Date();
        const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setReportMonth(defaultMonth);
        loadDashboard(defaultMonth);
    }, []);

    // Handle month change
    const handleMonthChange = (e) => {
        const selectedMonth = e.target.value;
        setReportMonth(selectedMonth);
        setMessage({ text: '', type: '' });
        loadDashboard(selectedMonth);
    };

    // Load dashboard data for all plants
    const loadDashboard = async (month) => {
        try {
            setLoading(true);
            setMessage({ text: '', type: '' });
            const data = await utilitiesAPI.getDashboard(month);
            if (data.plants && data.plants.length > 0) {
                setDashboardData(data.plants);
            } else {
                setDashboardData([]);
                setMessage({ text: 'No data available for this month', type: 'info' });
            }
        } catch (error) {
            setMessage({ text: 'Error loading dashboard data', type: 'error' });
            setDashboardData([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Excel export — calls the backend export route
    const handleExportExcel = async () => {
        if (!reportMonth || dashboardData.length === 0) {
            setMessage({ text: 'No data to export', type: 'error' });
            return;
        }
        try {
            setLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/export/utilities-dashboard/${reportMonth}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            if (!response.ok) throw new Error('Export failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Utilities_Dashboard_${reportMonth}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setMessage({ text: 'Dashboard exported successfully', type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            setMessage({ text: 'Error exporting dashboard', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Format number with commas
    const formatNumber = (num, decimals = 0) => {
        if (num === null || num === undefined || num === '' || isNaN(num)) return '-';
        return parseFloat(num).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Format month for display (e.g. "March 2026")
    const formatMonth = (monthStr) => {
        if (!monthStr) return '-';
        const [year, month] = monthStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    // Metric groups — colors match UtilitiesReportView column section colors
    const metricGroups = [
        {
            color: 'blue',
            metrics: [
                { key: 'pounds', label: 'Pounds', decimals: 0 }
            ]
        },
        {
            color: 'cyan',
            metrics: [
                { key: 'water_gallons',       label: 'Gallons',      decimals: 0 },
                { key: 'water_cost_per_gallon',label: 'Cost / Gallon',decimals: 4 },
                { key: 'water_total_cost',    label: 'Water * Cost', decimals: 0 },
                { key: 'gallon_per_lb',       label: 'Gallon / LB',  decimals: 4 }
            ]
        },
        {
            color: 'orange',
            metrics: [
                { key: 'gas_therms',          label: 'Therm',        decimals: 0 },
                { key: 'gas_cost_per_therm',  label: 'Cost / Therm', decimals: 4 },
                { key: 'gas_total_cost',      label: 'Gas * Cost',   decimals: 0 },
                { key: 'therm_per_lb',        label: 'Therm / LB',   decimals: 4 }
            ]
        },
        {
            color: 'yellow',
            metrics: [
                { key: 'electric_kwh',         label: 'KWH',           decimals: 0 },
                { key: 'electric_cost_per_kwh',label: 'Cost / KWH',    decimals: 4 },
                { key: 'electric_total_cost',  label: 'Electric * Cost',decimals: 0 },
                { key: 'kwh_per_lb',           label: 'KWH / LB',      decimals: 4 }
            ]
        },
        {
            color: 'green',
            metrics: [
                { key: 'total_cost_per_lb', label: 'Total Cost / LB', decimals: 4 }
            ]
        }
    ];

    return (
        <div className="main-dashboard-container">
            {/* ── Header: title + buttons + month picker ── */}
            <div className="dashboard-header-section">
                <div className="header-content">
                    <h2>Utilities Report</h2>
                    <p className="subtitle">{formatMonth(reportMonth)}</p>
                </div>

                <div className="header-controls">
                    {/* Print and Export buttons — same style as Flash Report dashboard */}
                    <div className="report-actions">
                        <button
                            onClick={() => window.print()}
                            className="btn-print"
                            disabled={loading || dashboardData.length === 0}
                        >
                            🖨️ Print
                        </button>
                        <button
                            onClick={handleExportExcel}
                            className="btn-export"
                            disabled={loading || dashboardData.length === 0}
                        >
                            📊 Export to Excel
                        </button>
                    </div>

                    <div className="date-selector">
                        <input
                            type="month"
                            id="dashboardReportMonth"
                            value={reportMonth}
                            onChange={handleMonthChange}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`message message-${message.type}`}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            ) : dashboardData.length > 0 ? (
                <div className="dashboard-table-wrapper">
                    <table className="dashboard-table">
                        <thead>
                            {/* Row 1: Plant name headers */}
                            <tr>
                                <th className="metric-header"></th>
                                {dashboardData.map(plant => (
                                    <th key={plant.plant_id} colSpan="2" className="plant-header">
                                        {plant.plant_name}
                                    </th>
                                ))}
                            </tr>
                            {/* Row 2: Current / Last 6 Month Avg sub-headers */}
                            <tr>
                                <th className="metric-header"></th>
                                {dashboardData.map(plant => (
                                    <React.Fragment key={plant.plant_id}>
                                        <th className="sub-header">
                                            Current<br/>{formatMonth(reportMonth)}
                                        </th>
                                        <th className="sub-header avg-col">
                                            Last 6 Month<br/>Avg
                                        </th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {metricGroups.map((group, groupIndex) => (
                                <React.Fragment key={groupIndex}>
                                    {group.metrics.map((metric, metricIndex) => (
                                        <tr
                                            key={metric.key}
                                            className={[
                                                `color-${group.color}`,
                                                metricIndex === 0 && groupIndex > 0 ? 'section-start' : ''
                                            ].filter(Boolean).join(' ')}
                                        >
                                            <td className="metric-label">{metric.label}</td>
                                            {dashboardData.map(plant => (
                                                <React.Fragment key={plant.plant_id}>
                                                    <td className="data-cell">
                                                        {formatNumber(plant.current_month?.[metric.key], metric.decimals)}
                                                    </td>
                                                    <td className="data-cell avg-col">
                                                        {formatNumber(plant.avg_6_month?.[metric.key], metric.decimals)}
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}
                                    {groupIndex < metricGroups.length - 1 && (
                                        <tr className="spacer-row">
                                            <td colSpan="100%"></td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="empty-state">
                    <p>No data available. Please enter data for plants in the Data Entry tab.</p>
                </div>
            )}
        </div>
    );
}

export default UtilitiesDashboard;
